<script setup lang="ts">
export interface FormCreditCardProps {
  cardHolderName?: string;
  cardNumber?: string;
  cardExpiration?: string;
  cardType?: TCreditCard;
  disabled?: boolean;
  submitLabel?: string;
  outline?: boolean;
}

const { t } = useI18n();
const emit = defineEmits(["submit"]);
const props = defineProps<FormCreditCardProps>();
const formData = ref({
  cardHolderName: props.cardHolderName || "",
  cardNumber: props.cardNumber || "",
  cardExpiration: props.cardExpiration || "",
  cardCVV: "",
  cardType: props.cardType || "",
} as IModelCreditCard);

const ccDefinitions: Record<TCreditCard, RegExp> = {
  visa: /^4/,
  mastercard: /^5[1-5]/,
  amex: /^3(4|7)/,
  discover: /^6011/,
};

const ccPaterns: Record<string, string> = {
  digit_15: "[0-9]{4}( )[0-9]{6}( )[0-9]{5}",
  digit_16: "([0-9]{4}( )){3}[0-9]{4}",
};

const onSubmit = () => emit("submit", formData.value);
const getCardType = (cardNumber?: string): TCreditCard | undefined => {
  if (!cardNumber)
    return;

  for (const cardType in ccDefinitions) {
    if (ccDefinitions[cardType as TCreditCard].test(cardNumber))
      return cardType as TCreditCard;
  }
};

const formatCardNumber = (cardNumber: string) => {
  let number = cardNumber.split(" ").join("");
  const { cardType } = formData.value;

  if (!cardNumber.length)
    return number;

  let matches;
  if (cardType === "amex") {
    matches = number.match(/^(\d{1,4})(\d{1,6})?(\d{1,5})?/);
    if (matches) {
      number = matches
        .slice(1) // remove the first element (the full match)
        .filter(Boolean)
        .join(" ")
        .replace(/\D/g, " ");
    }

    return number;
  }

  matches = number.match(/.{1,4}/g);
  if (matches) {
    number = matches
      .join(" ")
      .replace(/\D/g, " ");
  }

  return number;
};

const formatCardExpiration = (cardExpiration: string) => {
  let date = cardExpiration.split(" ").join("");
  const match = date.match(/^(\d{2})(\d{2})/);
  if (date.length && match)
    date = date.replace(/^(\d{2})(\d{2})/, "$1/$2");

  return date;
};

const creditCardPatternValidationByType = computed(() => {
  const { cardType } = formData.value;
  if (cardType === "amex")
    return ccPaterns.digit_15;

  return ccPaterns.digit_16;
});

const formatCard = () => {
  formData.value.cardNumber = formatCardNumber(formData.value.cardNumber);
};
const formatExpiration = () => {
  formData.value.cardExpiration = formatCardExpiration(formData.value.cardExpiration);
};
const updateCardType = (cardNumber: string) => (formData.value.cardType = getCardType(cardNumber));
</script>

<template>
  <form @submit.prevent="onSubmit()">
    <div class="flex mb-6">
      <IconCreditCardVisa />
      <IconCreditCardMasterCard class="mx-3" />
      <IconCreditCardAxp />
    </div>

    <BaseInput
      v-model="formData.cardHolderName"
      :outline="props.outline"
      :disabled="props.disabled"
      :label="t('pages.account_billing.card_holder_name')"
      root-class="mb-6"
      name="cardName"
      required
    />

    <BaseInput
      v-model="formData.cardNumber"
      :outline="props.outline"
      :disabled="props.disabled"
      :label="t('pages.account_billing.card_number')"
      root-class="mb-6"
      name="cardNumber"
      autocomplete="cc-number"
      placeholder="0000 0000 0000 0000"
      :pattern="creditCardPatternValidationByType"
      maxlength="19"
      required
      @keyup="formatCard"
      @update:model-value="updateCardType"
    >
      <template #suffix>
        <IconCreditCardType v-if="formData.cardType" :type="formData.cardType" />
      </template>
    </BaseInput>

    <div class="flex gap-6 mb-8">
      <BaseInput
        v-model="formData.cardExpiration"
        :outline="props.outline"
        :disabled="props.disabled"
        :label="t('pages.account_billing.card_expiry_date')"
        root-class="w-full"
        name="cardExpiry"
        placeholder="MM / YY"
        pattern="(0[1-9]|1[0-2])\/?([0-9]{4}|[2-9]{2})"
        maxlength="5"
        required
        @keyup="formatExpiration"
      />

      <BaseInput
        v-model="formData.cardCVV"
        :disabled="props.disabled"
        :label="t('pages.account_billing.card_cvv')"
        root-class="w-full"
        name="cardCVV"
        :outline="props.outline"
        autocomplete="cc-csc"
        placeholder="3 or 4 digits"
        pattern="^[0-9]{3,4}$"
        maxlength="4"
        sensitive
        required
      />
    </div>

    <slot name="before-submit" />

    <BaseButton class="font-bold" name="submit" :disabled="props.disabled" full-width>
      {{ props.submitLabel || t('labels.save') }}
    </BaseButton>
  </form>
</template>
